import { Maybe } from 'graphql/jsutils/Maybe'
import { ContentfulClientOptions, getContentfulClient, getWebClient } from '@/services/ApolloClient'
import { GetFranchisePageCollectionQuery } from '@/types/codegen-contentful'
import {
  GetFranchiseBySlugQuery,
  GetLightFranchiseBySlugQuery,
  TicketsStatusReference,
} from '@/types/codegen-federation'
import { getFirstCollectionItemAs } from '@/utils/contentful/collection-helpers'
import { logger } from '@/utils/logging'
import { formatOrderSuffix } from '@/utils/numbers'
import { TranslateFunction } from '@/utils/translate/translate-client'
import { ContentWithWatchableReasons, isInFreeAccess, isInGuildAccess, inTheaters } from '../ProjectPhaseService'
import { normalizeProjectSeasons } from '../ProjectsService/normalizeSeasons'
import { Episode, Project } from '../ProjectsService/types'
import { GET_FRANCHISE_BY_SLUG, GET_FRANCHISE_PAGE_COLLECTION, GET_LIGHT_FRANCHISE_BY_SLUG } from './queries'

/**
 * Extended type that handles both direct content and ContentSeries with firstWatchable or seasons
 */
type WatchableContentOrSeries = ContentWithWatchableReasons & {
  __typename?: string | null
  firstWatchable?: ContentWithWatchableReasons | null
  seasons?: {
    edges?: Array<{
      node?: {
        episodes?: {
          edges?: Array<{
            node?: ContentWithWatchableReasons | null
          } | null> | null
        } | null
      } | null
    } | null> | null
  } | null
}

/**
 * Helper to get the first episode's watchable content from a series
 */
const getFirstEpisodeContent = (content?: WatchableContentOrSeries | null): ContentWithWatchableReasons | null => {
  if (content?.__typename !== 'ContentSeries') return null

  // Try firstWatchable first
  if (content?.firstWatchable) return content.firstWatchable

  // Fall back to first episode from seasons
  const firstSeason = content?.seasons?.edges?.[0]?.node
  const firstEpisode = firstSeason?.episodes?.edges?.[0]?.node
  return firstEpisode ?? null
}

/**
 * Minimal type for theatrical release data needed for buildSubtitle
 */
type TheatricalReleaseForSubtitle = {
  region?: {
    releaseDate?: string | null
    ticketsStatus?: TicketsStatusReference | null
  } | null
}

interface GetFranchisePageBySlugArgs {
  slug: string
  opts: ContentfulClientOptions
}

export type FranchisePageData = {
  slug: string
  heroBackgroundVideo: Maybe<string>
  shopifyStore: Maybe<{
    storeUrl: Maybe<string>
    accessToken: Maybe<string>
    handle: Maybe<string>
    storeHref: Maybe<string>
  }>
  posterHref: Maybe<string>
  posterDesktop: Maybe<{
    item: Array<{
      url: string
      secure_url?: string
    }>
  }>
  posterTablet: Maybe<{
    item: Array<{
      url: string
      secure_url?: string
    }>
  }>
  posterMobile: Maybe<{
    item: Array<{
      url: string
      secure_url?: string
    }>
  }>
  projectsCollection: {
    items: Array<{
      slug: string
      franchiseWatchOrderText: string
    }>
  }
  logoOverride: Maybe<{
    item: Array<{
      url: string
      secure_url?: string
    }>
  }>
}

export type LightFranchise = NonNullable<GetLightFranchiseBySlugQuery>['franchiseBySlug']

export type FranchiseExtended = NonNullable<GetFranchiseBySlugQuery>['franchiseBySlug']

export const getFranchisePageBySlug = async ({
  slug,
  opts,
}: GetFranchisePageBySlugArgs): Promise<FranchisePageData | null> => {
  const contentfulClient = getContentfulClient({
    preview: opts.preview,
    locale: opts.locale,
  })

  try {
    const { data } = await contentfulClient.query<GetFranchisePageCollectionQuery>({
      query: GET_FRANCHISE_PAGE_COLLECTION,
      variables: {
        preview: opts.preview,
        slug,
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    })
    return getFirstCollectionItemAs<FranchisePageData>(data?.franchisePageCollection?.items)
  } catch (error) {
    logger().warn(`Error fetching franchise page by slug: ${slug}`, error)
    return null
  }
}

export const getFranchiseBySlug = async (slug: string, includePrelease: boolean): Promise<FranchiseExtended | null> => {
  const client = getWebClient()

  try {
    const { data } = await client.query<GetFranchiseBySlugQuery>({
      query: GET_FRANCHISE_BY_SLUG,
      variables: {
        slug,
        includePrelease,
      },
      errorPolicy: 'all',
    })
    const franchise = data?.franchiseBySlug ?? null

    // Normalize each project's seasons from title.seasons to project.seasons
    if (franchise?.projects) {
      franchise.projects = franchise.projects.map((project) => {
        if (!project) return project
        return normalizeProjectSeasons(project) ?? project
      })
    }

    return franchise
  } catch (error) {
    logger().warn(`Error fetching franchise by slug: ${slug}`, error)
    return null
  }
}

export const getLightFranchiseBySlug = async (slug: string): Promise<LightFranchise | null> => {
  const client = getWebClient()

  try {
    const { data } = await client.query<GetLightFranchiseBySlugQuery>({
      query: GET_LIGHT_FRANCHISE_BY_SLUG,
      variables: {
        slug,
      },
      errorPolicy: 'all',
    })
    const franchise = data?.franchiseBySlug ?? null

    return franchise
  } catch (error) {
    logger().warn(`Error fetching light franchise by slug: ${slug}`, error)
    return null
  }
}

export const formatDate = (date: Date, locale: string, options: Intl.DateTimeFormatOptions): string => {
  return date.toLocaleString(locale, options)
}

export const formatDateWithSuffix = (date: Date, locale: string): string => {
  return (
    date.toLocaleString(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' }) +
    (locale === 'en' ? formatOrderSuffix(date.getUTCDate()) : '')
  )
}

const getTheatricalSubtitle = (
  t: TranslateFunction,
  locale: string,
  releaseDate: Date,
  currentDate: Date,
  isCurrentlyInTheaters: boolean,
): string => {
  if (isCurrentlyInTheaters && releaseDate < currentDate) {
    return t('inTheatersNow', 'In Theaters Now')
  } else {
    return t('inTheatersOn', 'In Theaters {{ date }}', {
      date: formatDateWithSuffix(releaseDate, locale),
    })
  }
}

const getGuildAccessSubtitle = (
  t: TranslateFunction,
  locale: string,
  releaseDate: Date,
  currentDate: Date,
  isGuildMember: boolean,
): string => {
  if (releaseDate < currentDate && isGuildMember) {
    return t('nowStreaming', 'Now Streaming')
  } else if (releaseDate < currentDate) {
    return t('nowStreamingForGuildMembers', 'Now Streaming for Guild Members')
  } else {
    return t('streamingForGuildMembersDate', 'Streaming for Guild Members {{ date }}', {
      date: formatDateWithSuffix(releaseDate, locale),
    })
  }
}

/**
 * Helper function to extract guild access start date from watchableReasons
 */
const getGuildAccessStartDate = (watchableContent?: WatchableContentOrSeries | null): Date | null => {
  // For ContentSeries, use firstWatchable or first episode from seasons
  const contentToCheck =
    watchableContent?.__typename === 'ContentSeries' ? getFirstEpisodeContent(watchableContent) : watchableContent

  if (!contentToCheck?.watchableReasons) return null

  const guildReason = contentToCheck.watchableReasons.find((reason) => reason?.__typename === 'GuildAvailabilityAction')

  if (guildReason && 'startDatetime' in guildReason && guildReason.startDatetime) {
    return new Date(guildReason.startDatetime)
  }

  return null
}

/**
 * Builds a subtitle for a project based on its release status.
 *
 * @param t - Translation function
 * @param locale - Current locale
 * @param isGuildMember - Whether the user is a guild member
 * @param watchableContent - Optional watchable content for accurate streaming status checks (for ContentSeries, will use firstWatchable or first episode)
 * @param theatricalRelease - Optional theatrical release data for theatrical dates and status
 * @returns A subtitle string describing the project's availability status
 */
export const buildSubtitle = (
  t: TranslateFunction,
  locale: string,
  isGuildMember: boolean,
  watchableContent?: WatchableContentOrSeries | null,
  theatricalRelease?: TheatricalReleaseForSubtitle | null,
): string => {
  const currentDate = new Date()

  // For ContentSeries, use firstWatchable or first episode for streaming checks
  const contentToCheck =
    watchableContent?.__typename === 'ContentSeries' ? getFirstEpisodeContent(watchableContent) : watchableContent

  // Check streaming status first - streaming takes priority over past theatrical releases
  const isPubliclyStreaming = isInFreeAccess(contentToCheck)
  const isGuildStreaming = isInGuildAccess(contentToCheck)

  // If currently streaming (publicly or guild), show streaming status
  if (isPubliclyStreaming) {
    return t('nowStreaming', 'Now Streaming')
  }

  if (isGuildStreaming) {
    if (isGuildMember) {
      return t('nowStreaming', 'Now Streaming')
    } else {
      return t('nowStreamingForGuildMembers', 'Now Streaming for Guild Members')
    }
  }

  // Check for theatrical release information (only if not streaming)
  const theatricalReleaseDate = theatricalRelease?.region?.releaseDate
  const ticketStatus = theatricalRelease?.region?.ticketsStatus

  // If theatrical release exists and currently in theaters, use it for the subtitle
  if (theatricalReleaseDate) {
    const releaseDate = new Date(theatricalReleaseDate)
    const isCurrentlyInTheaters = inTheaters(ticketStatus)
    // Only show theatrical subtitle if actually in theaters or upcoming
    if (isCurrentlyInTheaters || releaseDate > currentDate) {
      return getTheatricalSubtitle(t, locale, releaseDate, currentDate, isCurrentlyInTheaters)
    }
  }

  // Check for guild access start date from watchableReasons (for upcoming guild releases)
  const guildAccessDate = getGuildAccessStartDate(watchableContent)

  // If guild access release exists and is in the future, show upcoming date
  if (guildAccessDate && guildAccessDate > currentDate) {
    return getGuildAccessSubtitle(t, locale, guildAccessDate, currentDate, isGuildMember)
  }

  return t('toBeAnnounced', 'To Be Announced')
}

export function getSeasonNameForP3M(
  item: Project,
  t: TranslateFunction,
  locale: string,
  releaseDate: Date,
): string | null {
  const monthIndex = releaseDate.getMonth()

  switch (monthIndex) {
    case 0:
    case 1:
      return `${t('early', 'Early')} ${releaseDate.toLocaleString(locale, { year: 'numeric', timeZone: 'UTC' })}`
    case 2:
    case 3:
    case 4:
      return `${t('spring', 'Spring')} ${releaseDate.toLocaleString(locale, { year: 'numeric', timeZone: 'UTC' })}`
    case 5:
    case 6:
    case 7:
      return `${t('summer', 'Summer')} ${releaseDate.toLocaleString(locale, { year: 'numeric', timeZone: 'UTC' })}`
    case 8:
    case 9:
      return `${t('fall', 'Fall')} ${releaseDate.toLocaleString(locale, { year: 'numeric', timeZone: 'UTC' })}`
    case 10:
    case 11:
      return `${t('holidays', 'Holidays')} ${releaseDate.toLocaleString(locale, {
        year: 'numeric',
        timeZone: 'UTC',
      })}`
    default:
      return null
  }
}

export const canStreamEpisode = (isGuildMember: boolean, episode?: Episode): boolean => {
  if (!episode) {
    return false
  }
  const now = new Date()
  return Boolean(
    (episode?.publiclyAvailableDate && new Date(episode.publiclyAvailableDate) <= now) ||
      (isGuildMember && episode?.earlyAccessDate && new Date(episode.earlyAccessDate) <= now),
  )
}
